import type { ITracking, PollInterpreter } from 'components-machine';
import React, { createContext } from 'react';
import type { Poll } from 'schema/poll';
import { ICState } from 'schema/state';
import { SafeHOC } from 'src/advancedTypes';
import { NewLoaderOverlay } from '../components/NewLoaderOverlay';
import { AuthContextProvider } from '../contexts/authContext';
import { ICStateActionsProvider } from '../entrypoints/root/ICStateActionsContext';
import { PollOverlays } from '../entrypoints/root/overlays';
import { TrackingProvider } from '../entrypoints/root/TrackingContext';
import { usePollInterpreter } from '../reducer/machine/react';
import { usePollInterpreterStatePredicate } from '../reducer/machine/utils';
import { isWebviewTemplate } from '../utils/template';

export interface WithContextInject {
  state: ICState;
  tracking: ITracking;
}

interface Context {
  tracking: ITracking;
  interpreter: PollInterpreter;
}

export const PollContext = createContext<Context>({
  interpreter: null as unknown as PollInterpreter,
  tracking: null as unknown as ITracking,
});

type WithContextProps = {
  poll: Poll;
};

const withContext: SafeHOC<WithContextInject, WithContextProps> = (Component, ..._) => {
  return function ComponentWithContext(props) {
    const { interpreter, state, poll, tracking } = usePollInterpreter({ poll: props.poll });

    const isRestoringPollsSiteStorage = usePollInterpreterStatePredicate(interpreter, 'restoringPollsSiteStorage');
    const isRestoringBeforeAuthState = usePollInterpreterStatePredicate(interpreter, 'restoringBeforeAuthState');
    const isStorageLoading = isRestoringPollsSiteStorage || isRestoringBeforeAuthState;
    const isTransforming = usePollInterpreterStatePredicate(interpreter, 'transforming');
    const isRegularAuthStateLoading = usePollInterpreterStatePredicate(interpreter, 'fetchingAuthState.loading');
    const isInitServerPassPoll = usePollInterpreterStatePredicate(interpreter, 'initServerPassPoll');

    const isWebview = isWebviewTemplate(props.poll.template);

    if (isStorageLoading || isTransforming || isRegularAuthStateLoading || isInitServerPassPoll) {
      return <NewLoaderOverlay isWebview={isWebview} />;
    }

    return (
      <PollContext.Provider value={{ interpreter, tracking }}>
        <TrackingProvider>
          <ICStateActionsProvider>
            <AuthContextProvider poll={poll}>
              <PollOverlays poll={poll}>
                <Component {...props} poll={poll} state={state} tracking={tracking} />
              </PollOverlays>
            </AuthContextProvider>
          </ICStateActionsProvider>
        </TrackingProvider>
      </PollContext.Provider>
    );
  };
};

export default withContext;
